import * as React from "react";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Button,
  CircularProgress,
  Divider,
  Fab,
  FormControl,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Radio,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import "./index.css";
import Logo from "../../../assets/images/logo.svg";
import ChatIcon from "../../../assets/images/Chat.svg";
import MemoryIcon from "../../../assets/images/Memory.svg";
import Messages from "./Messages";
import { MessageType } from "./Messages/types";
import { drawerWidth } from "../../../constants";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  useGenerateSummaryMutation,
  useGetConversationByIdQuery,
  useGetPreviousSearchQuery,
  useGetThreadMetadataQuery,
  useGetUserConversationsQuery,
  useHydrateMasterValuesQuery,
  useSearchMessagesQuery,
  useSyncMessagesQuery,
  useUpdateThreadMetadataMutation,
} from "../../../services/chat";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setConversations } from "../../../rtkSlices/conversationsSlice";
import { CustomButton } from "../../../components/Button";
import { IoIosArrowDown } from "react-icons/io";
import { RiOpenaiFill } from "react-icons/ri";
import { RiClaudeFill } from "react-icons/ri";
import ManageThread from "./ManageThread";
import { CiCircleQuestion, CiSearch } from "react-icons/ci";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog";
import { ReactComponent as DeepseekIcon } from "../../../assets/images/deepseek.svg";
import { errorToast, successToast } from "../../../utils/toast";
import Textbox from "./Textbox";
import ManageSummary from "./ManageSummary";
import { Slider, styled } from "@mui/material";
import {
  useTokenBalanceQuery,
  useTokenCostQuery,
} from "../../../services/token";
import { setTokenMetadata, setUser } from "../../../rtkSlices/metaDataSlice";
import { tokenBalanceDisplay, useDebounce } from "../../../utils/functions";
import { ReactComponent as BrainIcon } from "../../../assets/images/brain.svg";
import { ReactComponent as WaterIcon } from "../../../assets/images/droplet.svg";
import { SubscriptionDialog } from "../../../components/SubscriptionDialog";
import { ReactComponent as CoinSvg } from "../../../assets/images/coin.svg";
import LLMSelector from "./LLMSelector";
import SearchMessages from "./SearchMessages";
import { toast } from "react-toastify";
import { IoIosClose } from "react-icons/io";
import Onboarding from "../../onboarding";

export default function Chat() {
  const dispatch = useDispatch();
  const metaData = useSelector((state: any) => state.metaData);
  const paymentLinks = metaData.stripe.paymentLinks;
  const userId = metaData.userInfo["cognito:username"];
  const robotOptions = [
    { icon: ChatIcon, redirect: "/manage-memory" },
    { icon: MemoryIcon, redirect: "/manage-memory" },
  ];
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const tokenBalance = useTokenBalanceQuery({ userId });
  const tokenCost = useTokenCostQuery("");
  const [selectedOption, setSelectedOption] = React.useState(1);
  const navigate = useNavigate();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [robotFabEnable, setRobotFabEnable] = React.useState(false);
  const [updateOnboarding, setUpdateOnboarding] = React.useState(false);
  const bgdefault = theme.palette.background.default;
  const [messages, setMessages] = React.useState<MessageType[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversationId, setConversationId] = React.useState(
    searchParams.get("conversation_id")
  );
  const [generatedSummary, setGeneratedSummary] = React.useState("");
  let generatingSummary = false;

  const [manageSummary, setManageSummary] = React.useState("");
  const [currentConversationId, setCurrentConversationId] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [shownTokenWarningPopup, setShownTokenWarningPopup] =
    React.useState(false);
  const [threadMemoryAnchor, setThreadMemoryAnchor] =
    React.useState<HTMLDivElement | null>(null);
  const [hoverAge, setHoverAge] = React.useState(false);
  const hydrateMasterValues = useHydrateMasterValuesQuery("");
  const [maxTokens, setMaxTokens] = React.useState(0);
  const [totalTokensUsed, setTotalTokensUsed] = React.useState(0);
  const [tempSelectedLlm, setTempSelectedLlm] = React.useState(selectedOption);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [showSubscriptionPopup, setShowSubscriptionPopup] =
    React.useState(false);
  const [hydrateThread, setHydrateThread] = React.useState(false);
  let userInfo: any = localStorage.getItem("userInfo");
  userInfo = userInfo ? JSON.parse(userInfo) : null;
  const [agePercent, setAgePercent] = React.useState<number>(0);
  const [threadAge, setThreadAge] = React.useState(0);
  const [confirmationProps, setConfirmationProps] = React.useState<any>({
    title: "",
    content: "",
    btn1: "",
    btn2: "",
    handleBtn1: () => {},
    handleBtn2: () => {},
    icon: <div></div>,
    tooltip: "",
    bgcolor: "",
  });
  const [subscriptionProps, setSubscriptionProps] = React.useState<any>({
    title: "",
    content: "",
    btn1: "",
    handleBtn1: () => {},
    icon: <div></div>,
    bgimage: false,
    airhorn: false,
    height: "402px",
    width: "",
    subContent: "",
    cancel: () => {},
  });
  const [open, setOpen] = React.useState(Boolean(anchorEl));
  const [threadMetadata, setThreadMetadata] = React.useState<any>({});
  const getThreadMetaData = useGetThreadMetadataQuery(
    `?conversation_id=${searchParams.get("conversation_id")}`,
    { skip: !searchParams.get("conversation_id") }
  );
  const conversations = useSelector(
    (state: any) =>
      state.conversations.conversations
        ? [...state.conversations.conversations].reverse()
        : [],
    shallowEqual
  );
  const [updateThreadMetadata, updateThreadMetadataResult] =
    useUpdateThreadMetadataMutation();
  const [llmSelectorOptions, setLlmSelectorOptions] = React.useState([
    {
      id: 1,
      name: "OpenAI",
      value: "openai",
      logo: (
        <RiOpenaiFill
          color={"rgba(33, 143, 84, 1)"}
          size={20}
          cursor={"pointer"}
        />
      ),
      disabled: false,
    },
    {
      id: 2,
      name: "Claude",
      value: "claude",
      logo: (
        <RiClaudeFill
          color={"rgba(33, 143, 84, 1)"}
          size={20}
          cursor={"pointer"}
        />
      ),
      disabled: false,
    },
    {
      id: 3,
      name: "DeepSeek",
      value: "deepseek",
      logo: <DeepseekIcon className="deepseek-theme" />,
      disabled: false,
    },
  ]);
  const defaultDialogProps = () => {
    return {
      title: "",
      content: "",
      btn1: "",
      btn2: "",
      handleBtn1: () => {},
      handleBtn2: () => {},
      actions: true,
      icon: <div></div>,
      tooltip: "",
      bgcolor: "",
    };
  };
  let tempConfirmationProps = defaultDialogProps();
  let tempSubscriptionProps: {
    title: string;
    content: string | JSX.Element;
    btn1: string;
    handleBtn1: () => void;
    bgimage: boolean;
    icon: JSX.Element;
    airhorn: boolean;
    height: string;
    btncolor: string;
    subContent: string;
    cancel: (() => void) | undefined;
    cancelText: string;
    width: string;
  } = {
    title: "",
    content: "",
    btn1: "",
    handleBtn1: () => {},
    bgimage: false,
    icon: <div></div>,
    airhorn: false,
    height: "402px",
    btncolor: "#218F54",
    subContent: "",
    cancel: undefined,
    cancelText: "Later",
    width: "auto",
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(Boolean(event.currentTarget));
  };

  const getPaymentLink = (name: string) => {
    return paymentLinks.find((payment: { name: string | string[] }) =>
      payment.name.includes(name)
    );
  };

  const tokenPackContent = () => {
    const tokenPacks = [
      {
        title: "Quick fix",
        content: "10,000 Tokens",
        button: `Buy for $${getPaymentLink("10,000 Tokens").price}`,
        buttonHandler: () => {
          window.location.href = getPaymentLink("10,000 Tokens").url;
        },
        subText: "Light <b>top-up</b>",
      },
      {
        title: "On the Grind",
        content: "40,000 Tokens",
        button: `Buy for $${getPaymentLink("40,000 Tokens").price}`,
        buttonHandler: () => {
          window.location.href = getPaymentLink("40,000 Tokens").url;
        },
        subText: "Discount for <b>mid volume</b>",
      },
      {
        title: "Bo Mode",
        content: "100,000 Tokens",
        button: `Buy for $${getPaymentLink("100,000 Tokens").price}`,
        buttonHandler: () => {
          window.location.href = getPaymentLink("100,000 Tokens").url;
        },
        subText: "Best value for <b>heavy usage</b>",
      },
    ];
    return (
      <div className="token-packs">
        {tokenPacks.map((tokenPack, idx) => (
          <div className="token-pack" key={idx}>
            <div className="token-pack-coin">
              <CoinSvg width={"41px"} height={"41px"} />{" "}
            </div>
            <div className="token-pack-title">{tokenPack.title}</div>
            <div className="token-pack-content">{tokenPack.content}</div>
            <div style={{ minWidth: "70%" }}>
              <CustomButton
                text={tokenPack.button}
                color="white"
                bgcolor="#218F54"
                submit={tokenPack.buttonHandler}
                fontWeight="600"
                width="100%"
                height="50px"
                borderRadius="10px"
              />
            </div>
            <div
              className="token-subtext"
              dangerouslySetInnerHTML={{ __html: tokenPack.subText }}
            ></div>
          </div>
        ))}
      </div>
    );
  };

  React.useEffect(() => {
    if (!getThreadMetaData.isFetching && getThreadMetaData.isSuccess) {
      if (getThreadMetaData.data) {
        setThreadMetadata(getThreadMetaData.data);
      }
    }
  }, [getThreadMetaData.isFetching]);

  type SubscriptionProps = {
    title: string;
    content: string | JSX.Element;
    btn1: string;
    handleBtn1: () => void;
    bgimage: boolean;
    icon: JSX.Element;
    airhorn: boolean;
    height: string;
    btncolor: string;
    subContent: string;
    cancel: (() => void) | undefined;
    cancelText: string;
    width: string;
  };

  const enableBuyMoreTokensPrePopup = () => {
    const subsProps = {
      title: "Buy more tokens!",
      content: tokenPackContent(),
      icon: <CoinSvg width={"41px"} height={"41px"} />,
      bgimage: false,
      btn1: "",
      airhorn: false,
      btncolor: "#C7503D",
      handleBtn1: () => setShowSubscriptionPopup(false),
      height: "auto",
      width: "760px",
      cancel: () => setShowSubscriptionPopup(false),
      subContent: "",
      cancelText: "I'll think about it",
    };
    enableBuyMoreTokensPopup(subsProps);
  };

  const enableBuyMoreTokensPopup = ({
    title,
    content,
    btn1,
    handleBtn1,
    bgimage,
    icon,
    airhorn,
    height,
    btncolor,
    subContent,
    cancel,
    cancelText,
    width,
  }: SubscriptionProps) => {
    setShowSubscriptionPopup(true);
    tempSubscriptionProps = {
      title,
      content,
      icon,
      bgimage,
      btn1,
      airhorn,
      btncolor,
      handleBtn1,
      height,
      width,
      cancel,
      subContent,
      cancelText,
    };
    setSubscriptionProps(tempSubscriptionProps);
  };

  function stringAvatar(name: string) {
    return {
      sx: {
        bgcolor: "#2a945f",
      },
      children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
    };
  }

  React.useEffect(() => {
    if (!tokenBalance.isFetching && tokenBalance.isSuccess) {
      if (tokenBalance.data) {
        dispatch(setTokenMetadata({ tokenBalance: tokenBalance.data.balance }));
        if (tokenBalance.data.balance || tokenBalance.data.balance == 0) {
          const outOfTokens =
            tokenBalance.data.balance == 0
              ? true
              : tokenBalance.data.balance <= 100
              ? "almost"
              : null;
          if (outOfTokens && !shownTokenWarningPopup) {
            setShownTokenWarningPopup(true);
            const subsProps = {
              title:
                outOfTokens == "almost"
                  ? "You're almost out of tokens"
                  : "You're out of tokens",
              content: (
                <div>
                  {`${
                    outOfTokens == "almost"
                      ? `You have less than 100 tokens left. Buy more tokens to stay active!`
                      : "Keep using HiiBo with Alpha Monthly — get 20,000 tokens every month and unlock full access."
                  }`}
                </div>
              ),
              icon:
                outOfTokens == "almost" ? (
                  <></>
                ) : (
                  <CoinSvg width={"41px"} height={"41px"} />
                ),
              bgimage: false,
              btn1:
                outOfTokens == "almost"
                  ? "Buy more"
                  : userInfo.subscriptionStatus == "Freemium"
                  ? "Go Alpha 🚀 | Best Deal!"
                  : "Buy more",
              airhorn: false,
              btncolor: outOfTokens == "almost" ? "#C7503D" : "#218F54",
              handleBtn1: () => {
                if (userInfo.subscriptionStatus == "Freemium") {
                  window.location.href = `https://buy.stripe.com/test_5kQfZi8Mn1Vi7ik03ics804?prefilled_email=${userInfo.email}`;
                } else {
                  enableBuyMoreTokensPrePopup();
                }
              },
              height: "auto",
              width: "504px",
              cancel: () => setShowSubscriptionPopup(false),
              subContent:
                outOfTokens == "almost"
                  ? ""
                  : "🔥 <b>Bonus tokens</b> if you upgrade now!",
              cancelText:
                outOfTokens == "almost" ? "Later" : "I'll think about it",
            };
            enableBuyMoreTokensPopup(subsProps);
          }
        }
      }
    }
  }, [tokenBalance.isFetching]);

  React.useEffect(() => {
    if (!tokenCost.isFetching && tokenCost.isSuccess) {
      if (tokenCost.data) {
        dispatch(setTokenMetadata({ actionsTokensCost: tokenCost.data }));
      }
    }
  }, [tokenCost.isFetching]);

  React.useEffect(() => {
    if (userInfo && userInfo.personalThread) {
    }
  }, [userInfo]);

  const updateThreadLlm = async (newLlm: number) => {
    setSelectedOption(newLlm);
    let body: any = {
      conversation_id: searchParams.get("conversation_id") || "",
      user_id: userId,
      subject: threadMetadata.subject,
      generated_name: threadMetadata.generated_name,
      personality: (threadMetadata.personality.length
        ? threadMetadata.personality.split(",")
        : []
      ).join(","),
      temperature: threadMetadata.temperature,
      llm_choice: getLlmName(newLlm),
      context: threadMetadata.context,
    };
    const response: any = await updateThreadMetadata(body);
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
    } else {
      const allConversations = JSON.parse(
        JSON.stringify(conversations)
      ).reverse();
      if (!userInfo.personalThread) {
        allConversations.find((convo: any) => convo.selected).aiModel =
          getLlmName(newLlm);
        allConversations.find(
          (convo: any) => convo.selected
        ).metadata.llm_choice = getLlmName(newLlm);
        dispatch(setConversations(allConversations));
        refetch();
      }
      // getConversationById.refetch();
      setConversationId(searchParams.get("conversation_id"));
    }
  };

  const confirmHydrateThread = (hydrate = false) => {
    setAnchorEl(null);
    setOpen(false);
    setSelectedOption(tempSelectedLlm || selectedOption);
    if (!hydrate) {
      updateThreadLlm(tempSelectedLlm || selectedOption);
    }
    // setMessages([]);
    setManageSummary("");
    setConversationId("");
    setCurrentConversationId("");
    // navigate(`/chat`);
    // if (!hydrate) {
    //   setHydrateThread(true);
    // } else {
    setHydrateThread(false);
    // }
    tempConfirmationProps = defaultDialogProps();
  };

  const cancelHydrateThread = () => {
    setAnchorEl(null);
    setOpen(false);
    setTempSelectedLlm(selectedOption);
    setManageSummary("");
    tempConfirmationProps = defaultDialogProps();
  };

  React.useEffect(() => {
    if (!hydrateMasterValues.isFetching && hydrateMasterValues.isSuccess) {
      if (
        hydrateMasterValues.data &&
        hydrateMasterValues.data.length &&
        hydrateMasterValues.data.find(
          (d: any) => d.name == "ThreadAgeMaxTokensThreshold"
        )
      ) {
        setMaxTokens(
          hydrateMasterValues.data.find(
            (d: any) => d.name == "ThreadAgeMaxTokensThreshold"
          ).value
        );
        dispatch(
          setTokenMetadata({
            maxTokens: hydrateMasterValues.data.find(
              (d: any) => d.name == "UserMaxTokenThreshold"
            ).value,
          })
        );
      }
    }
  }, [hydrateMasterValues.isFetching]);

  const proceedToHydrate = async (keyword = "update") => {
    setConfirmationProps(tempConfirmationProps);
    await setManageSummary(keyword);
    tempConfirmationProps.btn2 = "Proceed";
    setConfirmationProps(tempConfirmationProps);
  };

  const GradientSlider = styled(Slider)({
    // height: 16,
    "& .MuiSlider-rail": {
      background:
        "linear-gradient(to right, #4CAF50,rgb(150, 157, 51), #FFD93D,rgb(255, 21, 0),rgb(220, 27, 27))", // Light Green → Green → Yellow → Orange → Dark Red
      opacity: 1,
      borderRadius: 4,
      height: 16,
    },
    "& .MuiSlider-track": {
      display: "none", // Hide track so gradient stays visible
    },
    "&.Mui-disabled": {
      pointerEvents: "all !important",
    },
    "& .MuiSlider-thumb": {
      width: 20,
      height: 16,
      backgroundColor: "#fff",
      borderRadius: "15px",
      border: "2px solid #4CAF50",
      "&:focus, &:hover, &.Mui-active": {
        boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.2)",
      },
    },
  });

  const getLlmName = (id: number) => {
    return llmSelectorOptions.find(
      (opt: { id?: number; name?: string }) => opt.id == id
    )?.value;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openUpdateOnboardingDetails = () => {
    setUpdateOnboarding(true);
  };

  const generateSummaryBeforeHydrate = () => {
    const tokens = metaData.tokenMetadata.actionsTokensCost?.generate_summary;
    setShowConfirmation(true);
    setConfirmationProps({
      title: "Confirmation",
      bgcolor: "#1a6e42",
      tooltip:
        "Hydrate Thread allows you to create a memory and refresh your AI.",
      content: `This action will consume "${tokens}" tokens, do you want to proceed?`,
      tokens,
      btn1: "Cancel",
      btn2: "Proceed",
      handleBtn1: () => {
        setShowConfirmation(false);
      },
      handleBtn2: () => {
        tempConfirmationProps = {
          title: "Confirmation",
          bgcolor: "#1a6e42",
          tooltip:
            "Hydrate Thread allows you to create a memory and refresh your AI.",
          content: `This action will consume "${tokens}" tokens, do you want to proceed?`,
          btn1: "Cancel",
          btn2: "",
          handleBtn1: () => setShowConfirmation(false),
          handleBtn2: () => {},
          actions: true,
          icon: <div></div>,
        };
        setConfirmationProps(tempConfirmationProps);
        setManageSummary("hydrate");
      },
      icon: <div></div>,
    });
  };

  const id = open ? "simple-popover" : undefined;
  React.useEffect(() => {
    setConversationId(searchParams.get("conversation_id"));
    if (!searchParams.get("conversation_id")) {
      setMessages([]);
      setCurrentConversationId("");
    } else {
      getThreadMetaData.refetch();
    }
  }, [searchParams.get("conversation_id")]);

  const getConversationById = useGetConversationByIdQuery(
    `?conversationId=${conversationId}&limit=50`,
    { skip: !conversationId, refetchOnMountOrArgChange: true }
  );

  const { data, isFetching, isSuccess, refetch } = useGetUserConversationsQuery(
    userId,
    { refetchOnMountOrArgChange: false }
  );

  const hydrateWarning = () => {
    toast(
      <div
        style={{ display: "flex", flexDirection: "column", alignItems: "left" }}
      >
        <div
          style={{
            margin: "5px 0px 10px 0px",
            display: "flex",
            gap: 5,
            alignItems: "center",
          }}
        >
          <Tooltip
            title={
              "When a thread gets too long, we hydrate it to help the AI remember. This keeps your context intact."
            }
            PopperProps={{
              sx: {
                zIndex: 9999999999999,
              },
            }}
          >
            <div>
              <CiCircleQuestion />
            </div>
          </Tooltip>{" "}
          <div>
            Your thread is below 40% capacity. Hydrate now to keep costs down
          </div>
          {/* <div className="clost-toast">
            <IoIosClose color="#2c4a3a" />
          </div> */}
        </div>
        <CustomButton
          text={manageSummary == "hydrate" ? "" : "Hydrate now"}
          bgcolor={"#9A3927"}
          borderRadius="10px"
          width="fit-content"
          color="white"
          fontSize="13px"
          submit={() => {
            if (metaData.tokenMetadata.tokenBalance >= 20) {
              setManageSummary("hydrate");
            } else {
              toast.dismiss();
              forceHydrate(metaData.tokenMetadata.tokenBalance);
            }
          }}
        />
      </div>,
      {
        autoClose: 50000,
        hideProgressBar: true,
        theme: "colored",
        position: "top-right",
        closeButton: true,
        className: `${"danger-toast"} hydrate-thread-warning`,
      }
    );
  };

  const forceHydrate = (tokens: number) => {
    setShowConfirmation(true);
    setConfirmationProps({
      title: "WARNING",
      bgcolor: "rgba(180, 69, 52, 1)",
      tooltip:
        tokens && tokens >= 20
          ? "When a thread gets too long, we hydrate it to help the AI remember. This keeps your context intact."
          : "Hydrating requires tokens to keep your AI conversation going. You’re out of tokens, but you can buy more to continue",
      content:
        tokens && tokens >= 20
          ? "Your Thread’s health is dropping. Hydrate now to keep Bo running at full brainpower. "
          : "Your Thread Health is too low. You don't have enough tokens to hydrate",
      btn1: "Cancel",
      btn2: tokens && tokens >= 20 ? "Hydrate" : "Buy tokens",
      tokens:
        tokens &&
        tokens >= 20 &&
        metaData.tokenMetadata.actionsTokensCost?.generate_summary,
      handleBtn1: () => {
        setShowConfirmation(false);
        setConfirmationProps({
          title: "",
          content: "",
          btn1: "",
          btn2: "",
          handleBtn1: () => {},
          handleBtn2: () => {},
          icon: <div></div>,
        });
      },
      handleBtn2: () => {
        tempConfirmationProps = {
          title: "WARNING",
          bgcolor: "rgba(180, 69, 52, 1)",
          tooltip:
            tokens && tokens >= 20
              ? "When a thread gets too long, we hydrate it to help the AI remember. This keeps your context intact."
              : "Hydrating requires tokens to keep your AI conversation going. You’re out of tokens, but you can buy more to continue",
          content:
            tokens && tokens >= 20
              ? "Your Thread’s health is dropping. Hydrate now to keep Bo running at full brainpower. "
              : "Your Thread Health is too low. You don't have enough tokens to hydrate",
          btn1: "Cancel",
          btn2: "",
          handleBtn1: () => {
            setShowConfirmation(false);
            setConfirmationProps({
              title: "",
              content: "",
              btn1: "",
              btn2: "",
              handleBtn1: () => {},
              handleBtn2: () => {},
              icon: <div></div>,
            });
          },
          actions: true,
          handleBtn2: () => {},
          icon: <div></div>,
        };
        if (tokens && tokens >= 20) {
          proceedToHydrate("hydrate");
        } else {
          tempConfirmationProps = defaultDialogProps();
          setConfirmationProps(tempConfirmationProps);
          setShowConfirmation(false);
          enableBuyMoreTokensPrePopup();
        }
      },
      icon: <div></div>,
    });
  };

  const handleProceedLlmSwitch = (option: any) => {
    setTempSelectedLlm(option.id);
    tempConfirmationProps = {
      title: "WARNING",
      bgcolor: "rgba(180, 69, 52, 1)",
      tooltip:
        "When you switch LLMs, we rehydrate the conversation so the new LLM can catch up",
      content:
        "Changing LLMs will require a HYDRATE action. Do you wish to proceed?",
      btn1: "Cancel",
      btn2: "",
      handleBtn1: () => setShowConfirmation(false),
      handleBtn2: () => {},
      actions: true,
      icon: <div></div>,
    };
    setConfirmationProps(tempConfirmationProps);
    proceedToHydrate();
  };

  React.useEffect(() => {
    if (!isFetching) {
      if (data && isSuccess) {
        const allConversations = JSON.parse(
          JSON.stringify(
            data.conversation.filter((convo: any) => convo.status == "active")
          )
        );
        // if (searchParams.get("conversation_id")) {
        if (allConversations && allConversations.length) {
          if (
            // !searchParams.get("conversation_id") &&
            !userInfo.personalThread
          ) {
            allConversations[allConversations.length - 1].selected = true;
            navigate(
              `/chat?conversation_id=${
                allConversations[allConversations.length - 1].conversation_id
              }`
            );
          }
          if (userInfo.personalThread && userInfo.personal_thread_id) {
            navigate(`/chat?conversation_id=${userInfo.personal_thread_id}`);
            setCurrentConversationId(userInfo.personal_thread_id);
            setConversationId(userInfo.personal_thread_id);
          }
        }
        // }
        dispatch(setConversations(allConversations));
      }
    }
  }, [isFetching]);

  const ageCheckForHydration = (totalTokens: any) => {
    if (totalTokens && maxTokens) {
      setAgePercent((totalTokens * 100) / maxTokens);
      const newPercent = 100 - (totalTokens * 100) / maxTokens;
      // if (newPercent == 70) {
      //   hydrateWarning(70);
      // }
      if (newPercent >= 37 && newPercent <= 40) {
        hydrateWarning();
      }
      if (newPercent == 10) {
        forceHydrate(metaData.tokenMetadata.tokenBalance);
      }
    }
  };

  React.useEffect(() => {
    if (!getConversationById.isFetching) {
      if (getConversationById.isSuccess) {
        const tempMsgs = getConversationById.data
          ? [...getConversationById.data.messages]
          : [];
        setThreadAge(getConversationById.data.totalTokensUsed);
        setTotalTokensUsed(getConversationById.data.totalTokensUsed);
        setAgePercent(
          (getConversationById.data.totalTokensUsed * 100) / maxTokens
        );
        setMessages(
          tempMsgs.filter(
            (msg) =>
              msg.role != "system" ||
              msg.messageContent == "Thread Hydrated" ||
              msg.messageContent == "LLM Switched"
          )
        );
        if (
          [
            ...getConversationById.data.messages.filter(
              (msg: any) =>
                msg.role != "system" ||
                msg.messageContent == "Thread Hydrated" ||
                msg.messageContent == "LLM Switched"
            ),
          ].length == 1
        ) {
          refetch();
        }
        ageCheckForHydration(getConversationById.data.totalTokensUsed);
        setGeneratedSummary("");
        const model =
          getConversationById.data.messages &&
          getConversationById.data.messages.length
            ? getConversationById.data.messages[
                getConversationById.data.messages.length - 1
              ].aiModel
            : "";
        if (model) {
          const option = llmSelectorOptions.find((opt) => opt.value == model);
          if (option) {
            setSelectedOption(option.id);
          }
        }
      }
    }
  }, [getConversationById.isFetching]);

  React.useEffect(() => {
    if (messages.length) {
      const chatElement: HTMLElement | null = document.getElementById("chat");
      if (chatElement) {
        chatElement.scrollTo(0, chatElement.scrollHeight);
      }
    }
  }, [JSON.stringify(messages)]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          background:
            userInfo && userInfo.personalThread
              ? "linear-gradient(126.73deg, rgba(52, 180, 116, 0.08) 0%, rgba(199, 255, 179, 0.3) 100.77%)"
              : bgdefault,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          height: "100%",
        }}
      >
        <div
          className="chat-header"
          style={{ width: `calc(100% - ${drawerWidth}px)`, zIndex: 999 }}
        >
          <div style={{ marginLeft: "15px" }}>
            <CustomButton
              text={
                selectedOption
                  ? llmSelectorOptions.find(
                      (opt: { id?: number; name?: string }) =>
                        opt.id == selectedOption
                    )
                    ? llmSelectorOptions.find(
                        (opt: { id?: number; name?: string }) =>
                          opt.id == selectedOption
                      )?.name
                    : ""
                  : "LLM Switcher"
              }
              submit={handleClick}
              bgcolor={"rgba(52, 180, 116, 0.25)"}
              // border="1px solid rgba(52, 180, 116, 0.25)"
              fontWeight="200"
              endIcon={<IoIosArrowDown />}
            />
            <Popover
              id={id}
              open={anchorEl ? true : false}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              sx={{ marginTop: "10px" }}
            >
              <div
                className="llm-selectors"
                style={{ backgroundColor: bgdefault }}
              >
                <LLMSelector
                  currentConversationId={currentConversationId}
                  conversationId={conversationId}
                  setShowConfirmation={setShowConfirmation}
                  setConfirmationProps={setConfirmationProps}
                  llmSelectorOptions={llmSelectorOptions}
                  setSelectedOption={setSelectedOption}
                  selectedOption={selectedOption}
                  setAnchorEl={setAnchorEl}
                  setOpen={setOpen}
                  handleProceedLlmSwitch={handleProceedLlmSwitch}
                />
              </div>
            </Popover>
          </div>
          <div className="thread-age">
            <div>
              <div className="thread-age">
                <ManageThread
                  setManageSummary={setManageSummary}
                  generatedSummary={generatedSummary}
                  threadMetadata={threadMetadata}
                  anchorEl={threadMemoryAnchor}
                  onClose={() => setThreadMemoryAnchor(null)}
                />
                <div>Thread health : </div>
                <div className="thread-count">
                  {" "}
                  {totalTokensUsed
                    ? `${(100 - (totalTokensUsed * 100) / maxTokens).toFixed(
                        2
                      )} %`
                    : totalTokensUsed == 0
                    ? "100%"
                    : ""}
                </div>
              </div>
              <GradientSlider
                defaultValue={
                  totalTokensUsed ? (totalTokensUsed * 100) / maxTokens : 0
                }
                min={0}
                max={100}
                disabled
                sx={{ pointerEvents: "auto" }}
                // valueLabelDisplay="auto"
              />
            </div>
            <div style={{ display: "flex", gap: "10px", margin: "-6px 5px" }}>
              <Tooltip
                placement="top"
                title={
                  userInfo && userInfo.personalThread
                    ? "Update your personality"
                    : "Thread Memory"
                }
              >
                <div
                  className="thread-icons"
                  onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    // setSelectedConversation("");
                    // navigate("/chat");
                    userInfo && userInfo.personalThread
                      ? openUpdateOnboardingDetails()
                      : setThreadMemoryAnchor(event.currentTarget);
                  }}
                >
                  <BrainIcon style={{ width: "29.17px", height: "23.23px" }} />
                </div>
              </Tooltip>
              <Tooltip placement="top" title={"Hydrate Thead"}>
                <div
                  className="thread-icons"
                  onClick={generateSummaryBeforeHydrate}
                >
                  <WaterIcon style={{ width: "29.79px", height: "34.17px" }} />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <SearchMessages
          wrapperRef={wrapperRef}
          setCurrentConversationId={setCurrentConversationId}
        />
        <div className="options-fab">
          {robotFabEnable
            ? robotOptions.map((option, i) => {
                return (
                  <div className="option-fab" key={i}>
                    <Fab
                      aria-label="robot"
                      sx={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: "#2a945f !important",
                      }}
                      disabled={i == 3 || i == 4}
                      onClick={() => navigate(option.redirect)}
                    >
                      <img src={option.icon} className="fab-icon" />
                    </Fab>
                  </div>
                );
              })
            : null}
        </div>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          <Toolbar />
          <div className="chat">
            <div className="chat-screen" id="chat" ref={wrapperRef}>
              {!messages.length ? (
                <div>
                  <img className="chat-bot-logo" src={Logo} />
                  <Typography
                    fontSize={"45px"}
                    fontWeight={"400"}
                    fontFamily={"Fredoka"}
                  >
                    {userInfo && userInfo.personalThread
                      ? "Your Private Thoughtpad 🤖"
                      : "Welcome to HiiBo!"}
                  </Typography>
                  {!(userInfo && userInfo.personalThread) ? (
                    <Typography
                      color="secondary"
                      fontWeight={"200"}
                      fontSize={20}
                      padding={"0px 20%"}
                    >
                      Select your AI engine (top-left), explore the Wizards on
                      the right for advanced tasks, chat below to ask me
                      anything, or just say ‘help me’ if you’re stuck
                    </Typography>
                  ) : null}
                </div>
              ) : (
                <Messages
                  messages={messages}
                  mode={mode}
                  selectedLlm={getLlmName(selectedOption)}
                />
              )}
            </div>
            <Textbox
              bgdefault={bgdefault}
              mode={mode}
              messages={messages}
              setMessages={setMessages}
              conversationId={conversationId}
              currentConversationId={currentConversationId}
              setCurrentConversationId={setCurrentConversationId}
              refetch={refetch}
              ai_llm={getLlmName(selectedOption)}
              generatedSummary={hydrateThread ? generatedSummary : ""}
              temperature={threadMetadata.temperature}
              agePercent={
                totalTokensUsed ? (totalTokensUsed * 100) / maxTokens : 0
              }
              ageCheckForHydration={ageCheckForHydration}
              setThreadAge={setThreadAge}
              switchLlm={updateThreadLlm}
              setHydrateThread={setHydrateThread}
              setSubscriptionProps={setSubscriptionProps}
              setShowSubscriptionPopup={setShowSubscriptionPopup}
              enableBuyMoreTokensPrePopup={enableBuyMoreTokensPrePopup}
            />
            {metaData.tokenMetadata &&
            metaData.tokenMetadata.maxTokens &&
            (metaData.tokenMetadata.tokenBalance ||
              metaData.tokenMetadata.tokenBalance == 0) ? (
              <div
                className="token-balance"
                onClick={enableBuyMoreTokensPrePopup}
              >
                {tokenBalanceDisplay(metaData.tokenMetadata.tokenBalance)}
              </div>
            ) : null}
          </div>
        </Box>
        {/* {showSearchMessages ? showSearchResults() : null} */}
        {showConfirmation ? (
          <ConfirmationDialog
            open={showConfirmation}
            content={confirmationProps.content}
            title={confirmationProps.title}
            btn1={confirmationProps.btn1}
            btn2={confirmationProps.btn2}
            handleBtn1={confirmationProps.handleBtn1}
            handleBtn2={confirmationProps.handleBtn2}
            icon={confirmationProps.icon}
            actions={confirmationProps.actions}
            tooltip={confirmationProps.tooltip}
            tokens={confirmationProps.tokens}
            bgcolor={confirmationProps.bgcolor}
          />
        ) : null}
        {showSubscriptionPopup ? (
          <SubscriptionDialog
            open={showSubscriptionPopup}
            content={subscriptionProps.content}
            title={subscriptionProps.title}
            btn1={subscriptionProps.btn1}
            handleBtn1={subscriptionProps.handleBtn1}
            icon={subscriptionProps.icon}
            bgimage={subscriptionProps.bgimage}
            btncolor={subscriptionProps.btncolor}
            cancel={subscriptionProps.cancel}
            airhorn={subscriptionProps.airhorn}
            height={subscriptionProps.height}
            width={subscriptionProps.width}
            subContent={subscriptionProps.subContent}
            cancelText={subscriptionProps.cancelText}
          />
        ) : null}

        {manageSummary ? (
          <ManageSummary
            convoId={currentConversationId || conversationId}
            ai_llm={
              llmSelectorOptions.find(
                (opt: { id?: number; name?: string }) =>
                  opt.id == selectedOption
              )?.value
            }
            new_llm={
              llmSelectorOptions.find(
                (opt: { id?: number; name?: string }) =>
                  opt.id == tempSelectedLlm
              )?.value
            }
            toast={toast}
            hydrate={manageSummary == "hydrate" ? true : false}
            confirmHydrateThread={confirmHydrateThread}
            cancelHydrateThread={cancelHydrateThread}
            generatedSummary={generatedSummary}
            setGeneratedSummary={(val: string) => {
              toast.dismiss();
              setGeneratedSummary(val);
            }}
            setShowConfirmation={setShowConfirmation}
            generatingSummary={generatingSummary}
          />
        ) : null}
      </Box>
      {(userInfo &&
        userInfo.subscriptionStatus &&
        !userInfo.isOnboardingCompleted) ||
      updateOnboarding ? (
        <Onboarding
          update={updateOnboarding}
          setUpdateOnboarding={setUpdateOnboarding}
          refetch={getConversationById.refetch}
        />
      ) : null}
    </>
  );
}
